// ---- SKU keys ---------------------------------------------------------------------------------------------------------------------
export const GEO_SKU = {
    googleRoutesEssentials: 'google.routes.compute.essentials',
    googleRoutesPro: 'google.routes.compute.pro',
    googleRoutesEnterprise: 'google.routes.compute.enterprise',
    googleMatrixEssentials: 'google.routes.matrix.essentials',
    googleMatrixPro: 'google.routes.matrix.pro',
    googleMatrixEnterprise: 'google.routes.matrix.enterprise',
    googleGeocoding: 'google.geocoding',
    googleAutocompleteRequests: 'google.places.autocomplete_requests',
    googleAutocompleteSessionUsage: 'google.places.autocomplete_session_usage',
    googleDetailsIdsOnly: 'google.places.details.ids_only',
    googleDetailsEssentials: 'google.places.details.essentials',
    googleDetailsPro: 'google.places.details.pro',
    googleDetailsEnterprise: 'google.places.details.enterprise',
    googleDetailsEnterpriseAtmosphere: 'google.places.details.enterprise_atmosphere',
    googleTextSearchPro: 'google.places.text_search.pro',
    googleDistanceMatrix: 'google.distance_matrix.basic',
    hereRoutingCar: 'here.routing.car',
    hereRoutingTimeAware: 'here.routing.time_aware',
    hereRoutingScooter: 'here.routing.scooter',
    hereGeocode: 'here.geocode',
    hereDiscover: 'here.discover',
    hereMatrix: 'here.matrix',
    osrmRouting: 'osrm.routing',
    osrmMatrix: 'osrm.matrix',
    nominatimGeocoding: 'nominatim.geocoding',
};
/** The stable reason of a pricing decision on a usage row. */
export const GEO_COST_REASON_CODES = [
    'GEO_COST_PRICED',
    'GEO_COST_FREE_ALLOWANCE',
    'GEO_COST_SELF_HOSTED_FREE',
    'GEO_COST_CACHED',
    'GEO_COST_SESSION_PENDING',
    'GEO_COST_SESSION_SETTLED',
    'GEO_COST_SESSION_ABANDONED',
    'GEO_COST_FAILED_NOT_BILLED',
    'GEO_COST_SKU_UNMAPPED',
    'GEO_COST_PRICE_BOOK_UNSET',
];
/** Google bills a session's Autocomplete requests at the per-request SKU only for the first 12 when the Details call is Essentials-only (research section 2.1). */
export const GOOGLE_AUTOCOMPLETE_BILLED_REQUESTS = 12;
/** The vendors that run on Vello's own servers: no vendor bill, their SKU is one zero band (`GEO_COST_SELF_HOSTED_FREE`). */
export const SELF_HOSTED_GEO_VENDORS = ['osrm', 'nominatim'];
/** The prediction `types` that make a suggestion an establishment / point of interest (its Place Details asks for the name, an Enterprise + Atmosphere SKU). */
export const GOOGLE_POI_TYPES = ['establishment', 'point_of_interest'];
export function isPoiTypes(types) {
    return types !== undefined && types.some((t) => GOOGLE_POI_TYPES.includes(t));
}
// ---- SKU from what the adapter sent -------------------------------------------------------------------------------------------------
/** Google Routes: two-wheeler routing is Enterprise; a traffic-aware preference or 11 to 25 intermediate waypoints is Pro; the rest Essentials. */
export function googleRouteSku(shape) {
    if (shape.travelMode === 'TWO_WHEELER')
        return GEO_SKU.googleRoutesEnterprise;
    if (shape.routingPreference === 'TRAFFIC_AWARE' || shape.routingPreference === 'TRAFFIC_AWARE_OPTIMAL' || shape.intermediates >= 11)
        return GEO_SKU.googleRoutesPro;
    return GEO_SKU.googleRoutesEssentials;
}
/** HERE Routing v8: a scooter has its own category; `departureTime=any` is the plain Routing category; no departure time (or a time) is Time Aware Routing. */
export function hereRouteSku(shape) {
    if (shape.transportMode === 'scooter')
        return GEO_SKU.hereRoutingScooter;
    return shape.departureTime === 'any' ? GEO_SKU.hereRoutingCar : GEO_SKU.hereRoutingTimeAware;
}
/** HERE Matrix Routing: when both counts are 5 or more the transactions are `5 x max(origins, destinations)`, else origins x destinations. */
export function hereMatrixUnits(origins, destinations) {
    return origins >= 5 && destinations >= 5 ? 5 * Math.max(origins, destinations) : origins * destinations;
}
/** Google Place Details: asking the name (a Pro field) bills the call as Enterprise + Atmosphere; an Essentials-only mask bills Essentials. */
export function googleDetailsSku(includesName) {
    return includesName ? GEO_SKU.googleDetailsEnterpriseAtmosphere : GEO_SKU.googleDetailsEssentials;
}
/**
 * What a settled Autocomplete session bills: with an Essentials Details call the first 12 requests at the per-request SKU and the rest as session usage (free); with a
 * Pro/Enterprise Details call every request is session usage (free). An abandoned session (no Details call) bills every request at the per-request SKU.
 */
export function googleSessionAutocompleteLines(requests, outcome) {
    if (requests <= 0)
        return [];
    if (outcome === 'abandoned')
        return [{ skuKey: GEO_SKU.googleAutocompleteRequests, units: requests }];
    if (outcome === 'enterprise')
        return [{ skuKey: GEO_SKU.googleAutocompleteSessionUsage, units: requests }];
    const billed = Math.min(requests, GOOGLE_AUTOCOMPLETE_BILLED_REQUESTS);
    return [
        { skuKey: GEO_SKU.googleAutocompleteRequests, units: billed },
        ...(requests > billed ? [{ skuKey: GEO_SKU.googleAutocompleteSessionUsage, units: requests - billed }] : []),
    ];
}
/** A call is billed when the vendor answered: a success, or a "nothing found" (HTTP 200 with no result; conservative - the invoice reconciliation settles it). */
export function isBilledOutcome(outcome) {
    return outcome === 'OK' || outcome === 'NOT_FOUND';
}
// ---- cumulative bands ---------------------------------------------------------------------------------------------------------------
/** The cost, in micros, of `units` taken from zero through the bands (the first band usually free). */
export function bandCostMicros(bands, units) {
    let remaining = units;
    let floor = 0n;
    let cost = 0n;
    for (const band of bands) {
        if (remaining <= 0n)
            break;
        const ceiling = band.upToUnits === null ? undefined : BigInt(band.upToUnits);
        const inBand = ceiling === undefined ? remaining : (ceiling - floor < remaining ? ceiling - floor : remaining);
        cost += inBand * BigInt(band.unitPriceMicros);
        remaining -= inBand;
        if (ceiling !== undefined)
            floor = ceiling;
    }
    return cost;
}
/** The cost of `units` more after `before` units were already counted this month. */
export function marginalCostMicros(bands, before, units) {
    return bandCostMicros(bands, before + units) - bandCostMicros(bands, before);
}
/** The size of the free allowance (the first band when its price is 0), else 0. */
export function freeAllowanceUnits(bands) {
    const first = bands[0];
    return first !== undefined && first.unitPriceMicros === 0 && first.upToUnits !== null ? BigInt(first.upToUnits) : 0n;
}
/** The unit price of the first PAID band (0 when every band is free) - what the unmapped fallback compares. */
function firstPaidPrice(bands) {
    return bands.find((b) => b.unitPriceMicros > 0)?.unitPriceMicros ?? 0;
}
/** A vendor's own book entry, or undefined when the book does not price the vendor. */
export function vendorBook(book, vendor) {
    return book.vendors[vendor];
}
/**
 * The pricing of one SKU. A SKU missing from a priced vendor is priced at that vendor's DEAREST SKU and flagged `unmapped`, so a call nobody mapped is never under-reported.
 * `undefined` when the book does not price the vendor at all (the caller keeps the legacy per-call cost).
 */
export function skuPricingOf(book, vendor, skuKey) {
    const v = vendorBook(book, vendor);
    if (v === undefined)
        return undefined;
    const own = v.skus[skuKey];
    if (own !== undefined)
        return { vendor: vendor, skuKey, currency: v.currency, bands: own.bands, unit: own.unit, unmapped: false };
    let dearest;
    for (const [key, sku] of Object.entries(v.skus))
        if (dearest === undefined || firstPaidPrice(sku.bands) > firstPaidPrice(dearest.sku.bands))
            dearest = { key, sku };
    if (dearest === undefined)
        return undefined;
    // No free allowance for a SKU nobody mapped: it is priced flat at the dearest SKU's first PAID price (never under-reported).
    return { vendor: vendor, skuKey, currency: v.currency, bands: [{ upToUnits: null, unitPriceMicros: firstPaidPrice(dearest.sku.bands) }], unit: dearest.sku.unit, unmapped: true };
}
// ---- the vendor's billing month ---------------------------------------------------------------------------------------------------------
/** The offset of `zone` from UTC at the instant `utcMs`, in ms (local = utc + offset). */
function zoneOffsetMs(utcMs, zone) {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(new Date(utcMs));
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    const local = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    return local - Math.floor(utcMs / 1000) * 1000;
}
/** The UTC instant (ms) at which the wall clock of `zone` reads the first instant of `year-month` (month 1 to 12). */
function monthStartUtcMs(year, month, zone) {
    const guess = Date.UTC(year, month - 1, 1);
    let t = guess - zoneOffsetMs(guess, zone);
    const second = guess - zoneOffsetMs(t, zone);
    if (second !== t)
        t = second;
    return t;
}
/** The `YYYY-MM` billing month an instant falls in, in the vendor's zone. */
export function billingMonthOf(atIso, zone) {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric', month: '2-digit' }).formatToParts(new Date(atIso));
    const year = parts.find((p) => p.type === 'year')?.value ?? '0000';
    const month = parts.find((p) => p.type === 'month')?.value ?? '01';
    return `${year}-${month}`;
}
/** The UTC window of a billing month in the vendor's zone: `[from, to)`. */
export function billingMonthWindow(month, zone) {
    const [y, m] = month.split('-').map(Number);
    const from = monthStartUtcMs(y, m, zone);
    const to = m === 12 ? monthStartUtcMs(y + 1, 1, zone) : monthStartUtcMs(y, m + 1, zone);
    return { from: new Date(from).toISOString(), to: new Date(to).toISOString() };
}
/** The previous `YYYY-MM`. */
export function previousMonth(month) {
    const [y, m] = month.split('-').map(Number);
    return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}
//# sourceMappingURL=pricing.js.map